import React from 'react';
import { useDispatch } from 'react-redux';
import { Navigate, useNavigate } from 'react-router-dom';
import classnames from 'classnames/bind';
import { toast } from 'react-toastify';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import { Button, Input } from '@miroculus/nucleo';
import { DASHBOARD_URL } from 'cons/routes';
import { AuthPage } from 'components';
import { canSubmit, getFirstError, getFieldError } from 'utils/forms';
import {
  createOrganization,
  loadOrganizations,
} from 'reduxModules/organizations';
import { useQuery } from 'hooks';
import styles from './CreateOrganization.scss';

const cx = classnames.bind(styles);

const CreateOrganizationSchema = Yup.object().shape({
  name: Yup.string()
    .min(3, 'The name is too short, try again')
    .required('A name is required'),
});

const CreateOrganization = () => {
  const query = useQuery();
  const token = query.get('token');
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const formik = useFormik({
    validateOnChange: false,
    validateOnBlur: true,
    initialValues: {
      name: '',
    },
    onSubmit: async (values, actions) => {
      actions.setStatus(undefined);
      try {
        await dispatch(createOrganization(values.name, token));
        await dispatch(loadOrganizations());
        navigate(DASHBOARD_URL);
      } catch (e) {
        actions.setStatus(e.message);
        console.error(e);
        toast.error(
          'Something went wrong creating the organization. Please contact support.'
        );
      }
    },
    validationSchema: CreateOrganizationSchema,
  });

  if (!token) return <Navigate to={DASHBOARD_URL} replace />;

  const errorToDisplay = getFirstError(formik);
  const statusMessage = errorToDisplay || formik.status;

  return (
    <AuthPage
      additionalInfo={
        <>
          You've been invited to create a new organization.
          <br />
          Please enter the organization name to get started:
        </>
      }
    >
      <form className={cx('form')} onSubmit={formik.handleSubmit}>
        <Input
          {...formik.getFieldProps('name')}
          name="name"
          type="text"
          placeholder="Enter the organization name"
          error={getFieldError(formik, 'name')}
          autoFocus
        />
        <div className={cx('buttonContainer')}>
          <p className={cx('statusMessage')}>{statusMessage || <>&nbsp;</>}</p>
          <Button type="submit" disabled={!canSubmit(formik)}>
            Create organization
          </Button>
        </div>
      </form>
    </AuthPage>
  );
};

export default CreateOrganization;
